<template>
  <!--
    Modified.by.KWShin.2026.10.15.
    시각 선택을 시/분/초 select-box 대신 DateTimeRangePickerNeo 처럼
    '시작 일시 / 종료 일시' 한 줄씩(시계 아이콘을 누르면 TimePicker)으로 바꾸고, 맨 아래에 '선택 완료' 버튼을 둔다.
    (select-box 용 시/분/초 목록(hours/minutes/seconds)과 그 위에 보이던 시작/종료 문구(begin/end)는 쓰는 곳이 없어 지웠다)
  -->
  <v-menu ref="menu" v-model="menu" :close-on-content-click="false" max-width="290" transition="scale-transition" offset-y>
    <template v-slot:activator="{ on, attrs }">
      <!-- eslint-disable-next-line prettier/prettier -->
      <v-text-field 
        v-model="range" 
        v-bind="attrs" 
        v-on="on" 
        :class="[disabledSeconds ? (clearable ? 'input-lg' : 'input-small') : clearable ? 'input-xlg' : 'input-lg']" 
        class="mr-1" 
        solo dense hide-details readonly
      >
        <template #append>
          <v-btn v-if="clearable" @click="onClear" class="icon" :class="{ 'primary--text': menu }" x-small text>
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-icon class="icon" :class="{ 'primary--text': menu }" @click="on.click">mdi-calendar</v-icon>
        </template>
      </v-text-field>
    </template>
    <!-- eslint-disable-next-line prettier/prettier -->
    <v-date-picker 
      v-model="dates" 
      ref="datepicker" 
      @change="change" 
      :max="max" 
      @click:date="pickQuick" 
      @click:month="pickQuick" 
      @click:year="pickQuick" 
      :day-format="date => new Date(date).getDate()" 
      color="primary" 
      range no-title scrollable
     >
      <v-card>
        <v-btn-toggle v-if="!disabledQuickPick" v-model="mode" class="d-flex" dense>
          <v-btn small @click="setType('DATE')">{{ $t('components.dtrp.day') }}</v-btn>
          <v-btn small @click="setType('DATE')">{{ $t('components.dtrp.week') }}</v-btn>
          <v-btn small @click="setType('MONTH')">{{ $t('components.dtrp.month') }}</v-btn>
          <v-btn small @click="setType('MONTH')">{{ $t('components.dtrp.quarter') }}</v-btn>
          <v-btn small @click="setType('YEAR')">{{ $t('components.dtrp.year') }}</v-btn>
        </v-btn-toggle>
        <v-menu v-model="bgnTimePicker" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <!-- eslint-disable-next-line prettier/prettier -->
            <v-text-field :value="beginText" v-bind="attrs" v-on="on"
              append-icon="mdi-clock-time-four-outline" @click:append="on.click"
              class="mr-1" :class="['input-time']" solo dense hide-details readonly
            >
              <template #prepend>
                <div class="print-section">{{ `${$t('cmpnt.begin.dt')} : ` }}</div>
              </template>
            </v-text-field>
          </template>
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-time-picker :value="getTime(0)" @input="setTime(0, $event)" @change="setTime(0, $event)"
            format="ampm" :use-seconds="!disabledSeconds" scrollable
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="bgnTimePicker = false">
              {{ $t('common.select.done') }}
            </v-btn>
          </v-time-picker>
        </v-menu>
        <v-menu v-model="endTimePicker" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <!-- eslint-disable-next-line prettier/prettier -->
            <v-text-field :value="endText" v-bind="attrs" v-on="on"
              append-icon="mdi-clock-time-four-outline" @click:append="on.click"
              class="mr-1" :class="['input-time']" solo dense hide-details readonly
            >
              <template #prepend>
                <div class="print-section">{{ `${$t('cmpnt.end.dt')} : ` }}</div>
              </template>
            </v-text-field>
          </template>
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-time-picker :value="getTime(1)" @input="setTime(1, $event)" @change="setTime(1, $event)"
            format="ampm" :use-seconds="!disabledSeconds" scrollable
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="endTimePicker = false">
              {{ $t('common.select.done') }}
            </v-btn>
          </v-time-picker>
        </v-menu>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="menu = false">
            {{ $t('common.select.done') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-date-picker>
  </v-menu>
</template>

<script>
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isEqual from 'date-fns/isEqual';
import isBefore from 'date-fns/isBefore';
import addHours from 'date-fns/addHours';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import lastDayOfWeek from 'date-fns/lastDayOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfQuarter from 'date-fns/startOfQuarter';
import endOfQuarter from 'date-fns/endOfQuarter';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';
import min from 'date-fns/min';
import set from 'date-fns/set';

const DAY_MODE = 0;
const WEEK_MODE = 1;
const MONTH_MODE = 2;
const QUARTER_MODE = 3;
const YEAR_MODE = 4;
/*
 * max 값 계산해서 디스플레이 및 emit 등에 적용
 */
export default {
  props: {
    value: {
      type: Array,
      default: () => {
        return [startOfDay(new Date()), endOfDay(new Date())];
      },
      // validator: value => {
      //   if (Array.isArray(value) && value.length === 2) {
      //     let valid = true;
      //     for (const date of value) {
      //       const type = Object.prototype.toString.call(date);
      //       if (type !== '[object Date]' && type !== '[object Number]') {
      //         valid = false;
      //         break;
      //       }
      //     }
      //     return valid;
      //   }
      //   return false;
      // },
    },
    disabledQuickPick: {
      type: Boolean,
      default: false,
    },
    disabledSeconds: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menu: false,
      // Added.by.KWShin.2026.10.15. 시작/종료 시각 선택기(TimePicker) 열림
      bgnTimePicker: false,
      endTimePicker: false,
      mode: null,
      max: format(new Date(), 'yyyy-MM-dd'),
      dates: this.getDates(),
      times: this.getTimes(),
      pattern: this.disabledSeconds ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd HH:mm:ss',
      origin: [],
    };
  },
  computed: {
    range() {
      const range = [];
      if (this.getBeginDateTime() !== null) {
        range.splice(0, 1, format(this.getBeginDateTime(), this.pattern));
      }
      if (this.getEndDateTime() !== null) {
        range.splice(1, 1, format(this.getEndDateTime(), this.pattern));
      }
      return range.join(' ~ ');
    },
    // BEGIN::Added.by.KWShin.2026.10.15. 시작/종료 일시 입력칸에 보이는 값(맨 위 기간 칸과 같은 형식)
    beginText() {
      return this.getBeginDateTime() !== null ? format(this.getBeginDateTime(), this.pattern) : '---';
    },
    endText() {
      return this.getEndDateTime() !== null ? format(this.getEndDateTime(), this.pattern) : '---';
    },
    // FINISH::Added.by.KWShin.2026.10.15.
  },
  watch: {
    value() {
      this.dates = this.getDates();
      this.times = this.getTimes();
    },
    menu(value) {
      if (!value) {
        if (this.getBeginDateTime() === null && this.getEndDateTime() === null) {
          this.emit(null);
        } else if (this.getEndDateTime() === null || isBefore(this.getEndDateTime(), this.getBeginDateTime())) {
          this.setEndDateTime(addHours(this.getBeginDateTime(), 1));
          this.emit();
        } else if (!isEqual(this.origin[0], this.getBeginDateTime()) || !isEqual(this.origin[1], this.getEndDateTime())) {
          this.emit();
        }
      }
    },
  },
  created() {
    this.origin = [this.getBeginDateTime(), this.getEndDateTime()];
  },
  methods: {
    // BEGIN::Added.by.KWShin.2026.10.15. TimePicker 와 시각 배열(times[index] = [HH, mm, ss]) 사이의 변환
    getTime(index) {
      return this.times[index].slice(0, this.disabledSeconds ? 2 : 3).join(':');
    },
    setTime(index, time) {
      if (!time) return;
      const [hh, mm, ss] = time.split(':');
      this.times.splice(index, 1, [hh, mm, this.disabledSeconds ? '00' : ss || '00']);
    },
    // FINISH::Added.by.KWShin.2026.10.15.
    getBeginDateTime() {
      return this.getDateTime(0);
    },
    setBeginDateTime(datetime) {
      this.setDateTime(0, datetime);
    },
    getEndDateTime() {
      return this.getDateTime(1);
    },
    setEndDateTime(datetime) {
      const md = min([parseISO(format(datetime, 'yyyy-MM-dd')), parseISO(this.max)]);
      datetime = set(datetime, { year: md.getFullYear(), month: md.getMonth(), date: md.getDate() });
      this.setDateTime(1, datetime);
    },
    getDateTime(index) {
      let date = this.dates[index];
      return date !== undefined ? parseISO(date + 'T' + this.times[index].join(':')) : null;
    },
    setDateTime(index, datetime) {
      this.dates.splice(index, 1, format(datetime, 'yyyy-MM-dd'));
      this.times[index].splice(0, 1, format(datetime, 'HH'));
      this.times[index].splice(1, 1, format(datetime, 'mm'));
      this.times[index].splice(2, 1, this.disabledSeconds ? '00' : format(datetime, 'ss'));
    },
    change() {
      if (isBefore(this.getEndDateTime(), this.getBeginDateTime())) {
        this.dates.reverse();
      }
    },
    emit(value = [this.getBeginDateTime(), this.getEndDateTime()]) {
      this.$emit('input', value);
      this.$emit('change', value);
    },
    setType(type) {
      this.$refs.datepicker.activePicker = type;
    },
    pickQuick(value) {
      if (this.mode != null) {
        const date = parseISO(this.mode === YEAR_MODE ? value + '-01-01' : value);
        let dates;
        if (this.mode === DAY_MODE) {
          dates = [date, date];
        }
        if (this.mode === WEEK_MODE) {
          dates = [startOfWeek(date), lastDayOfWeek(date)];
        }
        if (this.mode === MONTH_MODE) {
          dates = [startOfMonth(date), endOfMonth(date)];
        }
        if (this.mode === QUARTER_MODE) {
          dates = [startOfQuarter(date), endOfQuarter(date)];
        }
        if (this.mode === YEAR_MODE) {
          dates = [startOfYear(date), endOfYear(date)];
        }
        this.setBeginDateTime(startOfDay(dates[0]));
        this.setEndDateTime(endOfDay(dates[1]));
        this.mode = null;
        this.setType('DATE');
      }
    },
    getDates() {
      if (this.value !== null) {
        return [format(this.value[0], 'yyyy-MM-dd'), format(this.value[1], 'yyyy-MM-dd')];
      }
      return [];
    },
    getTimes() {
      if (this.value !== null) {
        let patterns = ['HH', 'mm'];
        if (!this.disabledSeconds) {
          patterns.push('ss');
        }
        const times = [[], []];
        for (let i = 0; i < 2; i++) {
          for (let k = 0; k < patterns.length; k++) {
            times[i][k] = format(this.value[i], patterns[k]);
          }
        }
        return times;
      }
      return [
        ['00', '00', '00'],
        ['00', '00', '00'],
      ];
    },
    onClear() {
      this.emit(null);
      this.$emit('click:clear');
    },
  },
};
</script>

<style lang="scss" scoped>
// Modified.by.KWShin.2026.10.15. 날짜 선택기의 버튼줄에만. (시작/종료 일시의 TimePicker 는 다른 선택기처럼 안쪽 여백을 둔다)
.v-picker--date ::v-deep .v-picker__actions {
  padding: 0;
}
.input-xlg {
  width: 305px;
}
.input-lg {
  width: 285px;
}
.input-small {
  width: 255px;
}
// Added.by.KWShin.2026.10.15. 시작/종료 일시 칸(DateTimeRangePickerNeo 와 같은 모양)
.input-time {
  width: 300px;
}
.time-area {
  padding: 9px 5px 9px 5px;
  max-width: 290px;
}
.print-section {
  margin: 0 0 5px 10px;
  width: 80px;
}
.time-section:not(:last-of-type) {
  margin-bottom: 8px;
}
.time-select {
  margin: 0 3px 0 3px;
}
.v-btn-toggle {
  border-radius: 0 0 4px 4px;
}
.v-btn:not(.v-btn--round).v-size--small {
  min-width: 58px;
}
.icon {
  padding: 0 !important;
}
</style>
